import { Link, NavLink, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import type { ViewMode } from "@/hooks/useViewMode";
import { brand } from "@/config/brand";
import { useAssistant } from "@/assistant/AssistantProvider";
import { AssistantMark } from "@/components/assistant/AssistantMark";
import { isActive, tabsFor } from "./nav";

/**
 * The same five destinations as the tab bar, as a left rail from 1024px
 * wide. SwiftUI: NavigationSplitView's sidebar on iPad.
 */
export function SideRail({ mode, unread = 0 }: { mode: ViewMode; unread?: number }) {
  const { pathname } = useLocation();
  const assistant = useAssistant();
  return (
    <nav aria-label="Main" className="fixed bottom-0 left-0 top-[var(--tfwp-demo-bar,0px)] z-40 hidden w-60 flex-col border-r border-line bg-surface px-3 pb-6 pt-6 lg:flex">
      <Link to="/" className="mb-8 flex items-center px-3" aria-label={`${brand.name}, home`}>
        <img src={brand.logoPath} alt={brand.name} className="h-9 w-auto" />
      </Link>
      <ul className="space-y-1">
        {tabsFor(mode).map((tab) => {
          const active = isActive(tab, pathname);
          const Icon = tab.icon;
          const badge = tab.to === "/messages" && unread > 0;
          return (
            <li key={tab.to}>
              <NavLink
                to={tab.to}
                aria-current={active ? "page" : undefined}
                aria-label={badge ? `${tab.label}, ${unread} unread` : tab.label}
                className={cn(
                  "flex h-11 items-center gap-3 rounded-lg px-3 text-callout font-semibold transition-colors duration-fast",
                  active ? "bg-tint-soft text-tint-strong" : "text-text-secondary hover:bg-surface-muted hover:text-text-primary",
                )}
              >
                <Icon className="h-5 w-5" strokeWidth={active ? 2.2 : 1.9} aria-hidden />
                <span className="flex-1">{tab.label}</span>
                {badge ? <span className="min-w-[20px] rounded-full bg-critical px-1.5 text-center text-caption2 font-bold leading-5 text-white">{unread > 9 ? "9+" : unread}</span> : null}
              </NavLink>
            </li>
          );
        })}
      </ul>
      {assistant.available ? (
        <button
          type="button"
          onClick={() => assistant.openSheet()}
          data-ask-open
          className="mt-auto flex items-center gap-3 rounded-xl border border-line bg-canvas p-3 text-left shadow-card hover:bg-surface-muted"
        >
          <AssistantMark size="md" />
          <span className="min-w-0">
            <span className="block text-headline text-text-primary">Ask</span>
            <span className="block truncate text-footnote text-text-tertiary">About your plan</span>
          </span>
        </button>
      ) : null}
    </nav>
  );
}
